import Highcharts from 'highcharts';

/**
 * Declarative chart description shared by every chart in the app.
 *
 * A `ChartSpec` says *what* to render; `buildChartOptions` turns it into a themed
 * `Highcharts.Options`. The wrapper (`<Chart>`) owns the Highcharts wiring so no
 * component repeats the module init / colour / credits / exporting boilerplate.
 *
 * Series are passed through raw as `Highcharts.SeriesOptionsType[]` so per-series
 * marker / dashStyle / zIndex / tooltip / events are preserved exactly.
 */

export type ChartColors = { text: string; [key: string]: unknown };

export type ChartAxisSpec = {
  title?: string;
  type?: Highcharts.AxisTypeValue;
  min?: number;
  max?: number;
  reversed?: boolean;
  plotBands?: Highcharts.XAxisPlotBandsOptions[];
  /** Categorical axis labels (column charts). */
  categories?: string[];
  crosshair?: boolean;
  /** Highcharts label `format` token, e.g. '{value:.2f}'. */
  labelsFormat?: string;
  minorTickInterval?: number | string;
  tickInterval?: number;
  endOnTick?: boolean;
  startOnTick?: boolean;
};

export type ChartLegendSpec =
  | boolean
  | {
      enabled?: boolean;
      fontSize?: string;
      symbolWidth?: number;
      symbolHeight?: number;
    };

type TooltipFormatter = (
  this: Highcharts.TooltipFormatterContextObject
) => string | false;

export type ChartTooltipSpec =
  | TooltipFormatter
  | {
      formatter?: TooltipFormatter;
      headerFormat?: string;
      pointFormat?: string;
      useHTML?: boolean;
    };

export type ChartSpec = {
  /** Maps to `chart.type`. Defaults to 'scatter'. */
  type?: string;
  title?: string;
  series: Highcharts.SeriesOptionsType[];
  xAxis?: ChartAxisSpec;
  yAxis?: ChartAxisSpec;
  tooltip?: ChartTooltipSpec;
  /** `true`/`false` or an object for extra legend styling. Defaults to enabled. */
  legend?: ChartLegendSpec;
  /** Series colour palette (maps to top-level `colors`). */
  colors?: string[];
  /** Convenience for a scatter point click handler. */
  onPointClick?: (event: Highcharts.PointClickEventObject) => void;
  height?: number | string;
  zoomType?: 'x' | 'y' | 'xy';
  margin?: { left?: number; right?: number; top?: number; bottom?: number };
  /** Defaults to true (Highcharts export toolbar). */
  exportEnabled?: boolean;
  /** Merged into the themed `plotOptions` for advanced cases. */
  plotOptions?: Highcharts.PlotOptions;
  /**
   * Enable the Highcharts boost module (canvas/WebGL) to cut SVG DOM nodes on
   * dense charts. `true` uses defaults; the object form tunes thresholds:
   *  - `threshold`        per-series point count above which that series boosts
   *  - `seriesThreshold`  chart-wide series count above which the whole chart boosts
   * Boosted series lose some per-point interactivity (individual markers/tooltips),
   * so do NOT enable it on charts that rely on point-click selection.
   */
  boost?: boolean | { threshold?: number; seriesThreshold?: number };
  /** Deep-merged over the built options last — escape hatch for exotic needs. */
  options?: Highcharts.Options;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Recursive merge where objects merge and arrays/primitives from `source` win. */
function deepMerge<T>(target: T, source: Partial<T> | undefined): T {
  if (!source) return target;
  const out: Record<string, unknown> = { ...(target as Record<string, unknown>) };
  for (const key of Object.keys(source as Record<string, unknown>)) {
    const sourceValue = (source as Record<string, unknown>)[key];
    const targetValue = out[key];
    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      out[key] = deepMerge(targetValue, sourceValue);
    } else {
      out[key] = sourceValue;
    }
  }
  return out as T;
}

// Returns `any` because Highcharts' axis option type is a union (X/Y/Z) and the
// same shape has to slot into both the xAxis and yAxis fields, whose label types
// differ. The original charts cast around this too.
function buildAxis(axis: ChartAxisSpec | undefined, colors: ChartColors): any {
  const a = axis ?? {};
  const out: any = {
    title: { text: a.title, style: { color: colors.text } },
    labels: {
      style: { color: colors.text },
      ...(a.labelsFormat ? { format: a.labelsFormat } : {})
    }
  };
  if (a.type !== undefined) out.type = a.type;
  if (a.min !== undefined) out.min = a.min;
  if (a.max !== undefined) out.max = a.max;
  if (a.reversed !== undefined) out.reversed = a.reversed;
  if (a.plotBands !== undefined) out.plotBands = a.plotBands;
  if (a.categories !== undefined) out.categories = a.categories;
  if (a.crosshair !== undefined) out.crosshair = a.crosshair;
  if (a.minorTickInterval !== undefined) out.minorTickInterval = a.minorTickInterval;
  if (a.tickInterval !== undefined) out.tickInterval = a.tickInterval;
  if (a.endOnTick !== undefined) out.endOnTick = a.endOnTick;
  if (a.startOnTick !== undefined) out.startOnTick = a.startOnTick;
  return out;
}

function buildLegend(
  legend: ChartLegendSpec | undefined,
  colors: ChartColors
): Highcharts.LegendOptions {
  const base: Highcharts.LegendOptions = {
    itemStyle: { color: colors.text },
    padding: 1,
    itemMarginTop: 1,
    itemMarginBottom: 1
  };
  if (legend === undefined) return base;
  if (typeof legend === 'boolean') return { ...base, enabled: legend };
  const { enabled, fontSize, symbolWidth, symbolHeight } = legend;
  return {
    ...base,
    ...(enabled !== undefined ? { enabled } : {}),
    itemStyle: { color: colors.text, ...(fontSize ? { fontSize } : {}) },
    ...(symbolWidth !== undefined ? { symbolWidth } : {}),
    ...(symbolHeight !== undefined ? { symbolHeight } : {})
  };
}

function buildTooltip(
  tooltip: ChartTooltipSpec | undefined
): Highcharts.TooltipOptions | undefined {
  if (!tooltip) return undefined;
  if (typeof tooltip === 'function') {
    return { useHTML: true, formatter: tooltip };
  }
  const { formatter, headerFormat, pointFormat, useHTML } = tooltip;
  return {
    ...(formatter ? { formatter } : {}),
    ...(headerFormat !== undefined ? { headerFormat } : {}),
    ...(pointFormat !== undefined ? { pointFormat } : {}),
    ...(useHTML !== undefined ? { useHTML } : formatter ? { useHTML: true } : {})
  };
}

/**
 * Pure mapping from a `ChartSpec` (+ theme colours) to a themed `Highcharts.Options`.
 * Kept separate from the React component so it can be unit-tested directly.
 */
export function buildChartOptions(
  spec: ChartSpec,
  colors: ChartColors
): Highcharts.Options {
  // `any` so chart-level extras like `zoomType` (typed elsewhere in Highcharts v11)
  // can be set without casting at each call site.
  const chart: any = {
    type: spec.type ?? 'scatter',
    backgroundColor: 'transparent'
  };
  if (spec.height !== undefined) chart.height = spec.height;
  if (spec.zoomType) chart.zoomType = spec.zoomType;
  if (spec.margin) {
    if (spec.margin.left !== undefined) chart.marginLeft = spec.margin.left;
    if (spec.margin.right !== undefined) chart.marginRight = spec.margin.right;
    if (spec.margin.top !== undefined) chart.marginTop = spec.margin.top;
    if (spec.margin.bottom !== undefined) chart.marginBottom = spec.margin.bottom;
  }

  let plotOptions: Highcharts.PlotOptions | undefined = spec.plotOptions
    ? { ...spec.plotOptions }
    : undefined;
  if (spec.onPointClick) {
    plotOptions = deepMerge(plotOptions ?? {}, {
      scatter: { point: { events: { click: spec.onPointClick } } }
    });
  }

  const tooltip = buildTooltip(spec.tooltip);

  let options: Highcharts.Options = {
    ...(spec.colors ? { colors: spec.colors } : {}),
    chart,
    title: { text: spec.title, style: { color: colors.text } },
    legend: buildLegend(spec.legend, colors),
    xAxis: buildAxis(spec.xAxis, colors),
    yAxis: buildAxis(spec.yAxis, colors),
    ...(tooltip ? { tooltip } : {}),
    ...(plotOptions ? { plotOptions } : {}),
    series: spec.series,
    exporting: { enabled: spec.exportEnabled ?? true },
    credits: { enabled: false }
  };

  if (spec.boost) {
    const b = spec.boost === true ? {} : spec.boost;
    (options as any).boost = {
      useGPUTranslations: true,
      usePreallocated: true,
      ...(b.seriesThreshold !== undefined ? { seriesThreshold: b.seriesThreshold } : {})
    };
    options.plotOptions = deepMerge(options.plotOptions ?? {}, {
      series: { boostThreshold: b.threshold ?? 1000 }
    } as Highcharts.PlotOptions);
  }

  if (spec.options) options = deepMerge(options, spec.options);
  return options;
}
